import { codeFont } from '../../utils/fonts';
import React, { useState, useCallback, useMemo } from 'react';
import { withTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Checkbox,
  Chip,
  CircularProgress,
  Alert,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  createFilterOptions,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { fi } from 'date-fns/locale';
import { parseISO, format as formatDate, isValid } from 'date-fns';
import {
  InputAdornment,
  Menu,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import UndoIcon from '@mui/icons-material/Undo';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AddIcon from '@mui/icons-material/Add';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import SellIcon from '@mui/icons-material/Sell';
import TranslateIcon from '@mui/icons-material/Translate';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import { useUserStore } from 'store';
import { getCsrfToken } from '../../utils';
import DecimalField from '../shared/DecimalField';
import { DEFAULT_VAT_RATE, calcPriceExclVat, calcPriceInclVat, formatPrice as formatVatPrice } from '../../utils/vat';
import { UNIT_OPTIONS, divideUnitPrice } from '../../utils/units';
import VatModeToggle from '../shared/VatModeToggle';

const vendorFilter = createFilterOptions();

// Package → content (1 tynnyri received as 20 l). Content mode books the row
// as the package's content: quantity × size, the CURRENT per-package price
// divided by the size (a corrected barrel price is kept), the content unit,
// and for a new part the name without the size token. The package values are
// stashed at that moment so package mode can restore them.
function applyBookingMode(line, mode) {
  const content = line.package_content;
  if (!content) return { ...line, booking_mode: 'package' };
  if (mode === 'content') {
    if (line.booking_mode === 'content') return line;
    return {
      ...line,
      booking_mode: 'content',
      package_quantity: line.quantity,
      package_unit_price: line.unit_price,
      package_name: line.name,
      // "20", not "20.00": the field shows what was typed, and 20 l reads better.
      quantity: String(parseFloat(content.booked_quantity)),
      unit_price:
        line.unit_price != null && line.unit_price !== '' ? divideUnitPrice(line.unit_price, content.quantity) : line.unit_price,
      unit: content.unit,
      name: line.match_status === 'no_match' ? content.content_name : line.name,
    };
  }
  if (line.booking_mode !== 'content') return { ...line, booking_mode: 'package' };
  return {
    ...line,
    booking_mode: 'package',
    quantity: line.package_quantity,
    unit_price: line.package_unit_price,
    name: line.package_name,
    unit: line.matched_part_unit || 'kpl',
    matched_part_id: line.package_part_id ?? line.matched_part_id,
    content_part: null,
  };
}

function packageUnit(line) {
  return line.matched_part_unit || 'kpl';
}

// The unit the line will be booked in differs from the matched part's: the
// stock lands on a separate content part (found, picked or created).
function unitDiffers(line) {
  return !!line.unit && line.unit !== packageUnit(line);
}

function SmartReceiveDialog({ t, i18n, open, onClose, onSuccess, mode = 'receive' }) {
  const isReturn = mode === 'return';
  const { currentOrganization } = useUserStore();

  // Phase: 'upload' or 'review'
  const [phase, setPhase] = useState('upload');

  // Upload phase state
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  // Review phase fields (populated from AI extraction, editable)
  const [vendor, setVendor] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [vendorLoading, setVendorLoading] = useState(false);
  const [vendorInputValue, setVendorInputValue] = useState('');
  const [creatingVendor, setCreatingVendor] = useState(false);
  const [extractedVendorName, setExtractedVendorName] = useState(null);
  const [referenceNumber, setReferenceNumber] = useState('');
  const [namePrefix, setNamePrefix] = useState('');
  const [transactionDate, setTransactionDate] = useState(new Date());

  // Review phase state. Each line carries its own price_includes_vat flag from
  // the AI — a document can mix alv 0% and sis. alv rows, so there is no single
  // dialog-level truth. Prices are sent exactly as shown; the backend converts.
  const [extractionId, setExtractionId] = useState(null);
  const [lines, setLines] = useState([]);
  const [selectedLines, setSelectedLines] = useState({});
  const [vatSwapped, setVatSwapped] = useState(false);
  const [priceIsSellPrice, setPriceIsSellPrice] = useState(false);
  const [sellPrefilled, setSellPrefilled] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [namesTranslated, setNamesTranslated] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState(null);

  const orgId = currentOrganization?.organization_id;

  // --- Vendor search ---
  const fetchVendors = useCallback(async (search) => {
    if (!orgId) return;
    setVendorLoading(true);
    try {
      const params = new URLSearchParams({ organization: orgId });
      if (search) params.append('search', search);
      const response = await axios.get(`/api/vendors/?${params.toString()}`);
      setVendors(response.data.results || response.data || []);
    } catch (err) {
      console.error('Failed to fetch vendors:', err);
    } finally {
      setVendorLoading(false);
    }
  }, [orgId]);

  const handleVendorInputChange = useCallback((event, newInputValue) => {
    setVendorInputValue(newInputValue);
    fetchVendors(newInputValue);
  }, [fetchVendors]);

  const handleVendorChange = useCallback(async (event, newValue) => {
    if (newValue && newValue._isNew) {
      // Create new vendor on the fly
      setCreatingVendor(true);
      try {
        const response = await axios.post(
          `/api/vendors/?organization=${orgId}`,
          { name: newValue.name },
          { headers: { 'X-CSRFToken': getCsrfToken() } }
        );
        const created = response.data;
        setVendor(created);
        setVendorInputValue(created.name);
        // Refresh vendor list so it appears in options
        fetchVendors('');
      } catch (err) {
        console.error('Failed to create vendor:', err);
      } finally {
        setCreatingVendor(false);
      }
    } else {
      setVendor(newValue);
    }
  }, [orgId, fetchVendors]);

  // --- File upload ---
  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      setFile(acceptedFiles[0]);
      setUploadError(null);
    }
  }, []);

  // Auto-upload when file is selected
  React.useEffect(() => {
    if (file && phase === 'upload' && !uploading) {
      handleUpload();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/webp': ['.webp'],
      'application/pdf': ['.pdf'],
      'text/csv': ['.csv'],
    },
    maxFiles: 1,
    maxSize: 20 * 1024 * 1024,
  });

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setUploadError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const endpoint = isReturn ? 'smart-return' : 'smart-receive';
      const response = await axios.post(
        `/api/inventory/${endpoint}/?organization=${orgId}`,
        formData,
        {
          headers: {
            'X-CSRFToken': getCsrfToken(),
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      setExtractionId(response.data.extraction_id);
      // Normalize an AI quirk: a VAT-inclusive flag with rate 0 promises a
      // conversion that could never run (and the backend now rejects it).
      const respLines = (response.data.lines || []).map((l) => ({
        ...(l.price_includes_vat && !(l.vat_rate > 0)
          ? { ...l, vat_rate: DEFAULT_VAT_RATE }
          : l),
        // Optional sell price: empty = untouched (margin pricing / existing
        // sell price stay as they are). Only a typed or prefilled value is
        // sent, so an existing Part.sell_price is never overwritten silently.
        sell_price: '',
        sell_price_includes_vat: currentOrganization?.default_price_includes_vat ?? false,
        unit: l.matched_part_unit || 'kpl',
        booking_mode: 'package',
        package_part_id: l.matched_part_id ?? null,
        content_part: null,
      })).map((l) =>
        // The litre twin already exists: it is the memory, so content mode is
        // preselected. A row never converted opens in the document's unit.
        !isReturn && l.package_content && l.package_content.content_part_info ? applyBookingMode(l, 'content') : l
      );
      setLines(respLines);
      setVatSwapped(false);
      setPriceIsSellPrice(false);
      setSellPrefilled(false);
      setNamesTranslated(false);

      // Select lines by default: in return mode, only select inventory_exists
      const selected = {};
      respLines.forEach((line) => {
        selected[line.line_index] = isReturn
          ? line.match_status === 'inventory_exists'
          : true;
      });
      setSelectedLines(selected);

      // Populate fields from document_info
      const docInfo = response.data.document_info;
      if (docInfo) {
        // Vendor
        if (docInfo.matched_vendor) {
          setVendor(docInfo.matched_vendor);
          setVendorInputValue(docInfo.matched_vendor.name);
        }
        setExtractedVendorName(docInfo.vendor_name || null);

        // Reference
        setReferenceNumber(docInfo.reference_number || '');

        // Date - use extracted date or fall back to today
        if (docInfo.document_date) {
          const parsed = parseISO(docInfo.document_date);
          if (isValid(parsed)) {
            setTransactionDate(parsed);
          } else {
            setTransactionDate(new Date());
          }
        } else {
          setTransactionDate(new Date());
        }
      }

      // Fetch vendors for the autocomplete in review phase
      fetchVendors('');
      setPhase('review');
    } catch (err) {
      console.error('Smart receive upload failed:', err);
      const errorMsg = err.response?.data?.error || t('inventory.smartReceive.extractionFailed');
      setUploadError(errorMsg);
    } finally {
      setUploading(false);
    }
  };

  // --- Review phase ---
  const handleLineToggle = (lineIndex) => {
    setSelectedLines((prev) => ({
      ...prev,
      [lineIndex]: !prev[lineIndex],
    }));
  };

  const handleLineEdit = (lineIndex, field, value) => {
    setLines((prev) =>
      prev.map((line) =>
        line.line_index === lineIndex ? { ...line, [field]: value } : line
      )
    );
  };

  const handleBookingModeChange = (lineIndex, mode) => {
    setLines((prev) => prev.map((line) => (line.line_index === lineIndex ? applyBookingMode(line, mode) : line)));
  };

  // Generic unit override (the "kpl" adornment on the quantity field). Back to
  // the matched part's own unit restores the package part as the target.
  const [unitMenu, setUnitMenu] = useState(null);
  const handleUnitPick = (lineIndex, unit) => {
    setLines((prev) =>
      prev.map((line) => {
        if (line.line_index !== lineIndex) return line;
        const next = { ...line, unit };
        if (unit === packageUnit(line)) {
          next.matched_part_id = line.package_part_id ?? line.matched_part_id;
          next.content_part = null;
        }
        return next;
      })
    );
    setUnitMenu(null);
  };

  // An existing same-unit part picked as the content target.
  const handleContentPartPick = (lineIndex, part) => {
    setLines((prev) =>
      prev.map((line) =>
        line.line_index === lineIndex
          ? { ...line, content_part: part, matched_part_id: part ? part.id : line.package_part_id ?? line.matched_part_id }
          : line
      )
    );
  };

  // Ensure prefix ends with " - " separator when set
  const formattedPrefix = useMemo(() => {
    const trimmed = namePrefix.trim();
    if (!trimmed) return '';
    if (trimmed.endsWith('-') || trimmed.endsWith('–') || trimmed.endsWith(':')) {
      return trimmed + ' ';
    }
    return trimmed + ' - ';
  }, [namePrefix]);

  const selectedCount = useMemo(() => {
    return Object.values(selectedLines).filter(Boolean).length;
  }, [selectedLines]);

  const statusCounts = useMemo(() => {
    const counts = { inventory_exists: 0, part_exists: 0, no_match: 0 };
    lines.forEach((line) => {
      if (counts[line.match_status] !== undefined) {
        counts[line.match_status]++;
      }
    });
    return counts;
  }, [lines]);

  // True when the document had a second price column the AI could offer instead
  const hasAlternatePrice = useMemo(
    () => lines.some((l) => l.list_price !== null && l.list_price !== undefined),
    [lines]
  );

  // Aggregate VAT view across lines: 'incl' | 'excl' | 'mixed'. Mixed documents
  // are real (alv 0% purchase column next to sis. alv OVH), so the header
  // toggle shows indeterminate instead of pretending one flag fits all.
  const vatSummary = useMemo(() => {
    if (lines.length === 0) return 'excl';
    const incl = lines.filter((l) => !!l.price_includes_vat).length;
    if (incl === lines.length) return 'incl';
    if (incl === 0) return 'excl';
    return 'mixed';
  }, [lines]);

  const setAllLinesVat = (includesVat) => {
    setLines((prev) =>
      prev.map((line) => ({
        ...line,
        price_includes_vat: includesVat,
        // Flipping to inclusive on a 0%-rate line would be a server-side
        // no-op while the UI promises a 25.5% conversion.
        vat_rate: includesVat && !(line.vat_rate > 0) ? DEFAULT_VAT_RATE : line.vat_rate,
      }))
    );
  };

  // Aggregate VAT view for the sell-price column. No vat_rate fixup needed:
  // the sell conversion always runs at the sales VAT rate server-side.
  const sellVatSummary = useMemo(() => {
    if (lines.length === 0) return 'excl';
    const incl = lines.filter((l) => !!l.sell_price_includes_vat).length;
    if (incl === lines.length) return 'incl';
    if (incl === 0) return 'excl';
    return 'mixed';
  }, [lines]);

  const setAllLinesSellVat = (includesVat) => {
    setLines((prev) =>
      prev.map((line) => ({ ...line, sell_price_includes_vat: includesVat }))
    );
  };

  // --- Batch action: prefill sell prices from the document's OVH column ---
  // One-shot: after prefilling, users edit or clear individual fields. Hidden
  // while the price columns are swapped (list_price then holds the cost).
  const handleSellPrefill = () => {
    setLines((prev) =>
      prev.map((line) => {
        if (line.list_price === null || line.list_price === undefined) return line;
        if (line.part_is_margin_vat_scheme) return line;
        return {
          ...line,
          sell_price: String(line.list_price),
          sell_price_includes_vat: line.list_price_includes_vat ?? line.sell_price_includes_vat,
        };
      })
    );
    setSellPrefilled(true);
  };

  // --- Batch action: VAT interpretation swap ---
  // Flips each line's own flag; displayed prices stay unchanged. The backend
  // does the conversion, so nothing is lost by toggling twice.
  const handleVatSwap = () => {
    setLines((prev) =>
      prev.map((line) => {
        const nowIncl = !line.price_includes_vat;
        return {
          ...line,
          price_includes_vat: nowIncl,
          vat_rate: nowIncl && !(line.vat_rate > 0) ? DEFAULT_VAT_RATE : line.vat_rate,
        };
      })
    );
    setVatSwapped((prev) => !prev);
  };

  // --- Batch action: price column swap (purchase <-> retail/OVH) ---
  // Used when the AI picked the wrong column off a document showing both.
  // Each price brings its own VAT flag along: on Finnish documents the
  // purchase column is typically alv 0% while OVH is sis. alv, so swapping
  // the numbers without their flags would misprice every line. Lines with a
  // single price column are left untouched.
  const handlePriceTypeSwap = () => {
    setLines((prev) =>
      prev.map((line) => {
        if (line.list_price === null || line.list_price === undefined) return line;
        const newIncl = line.list_price_includes_vat ?? line.price_includes_vat;
        return {
          ...line,
          unit_price: line.list_price,
          list_price: line.unit_price,
          // Unknown list-price flag (older cache, AI omission): keep the
          // current interpretation rather than inventing one.
          price_includes_vat: newIncl,
          list_price_includes_vat: line.price_includes_vat,
          vat_rate: newIncl && !(line.vat_rate > 0) ? DEFAULT_VAT_RATE : line.vat_rate,
        };
      })
    );
    setPriceIsSellPrice((prev) => !prev);
  };

  // --- Batch action: translate part names ---
  const handleTranslateNames = async () => {
    const names = lines.map((l) => l.name);
    if (names.length === 0) return;

    setTranslating(true);
    setConfirmError(null);
    try {
      const response = await axios.post(
        '/api/parts/translate-names/',
        {
          names,
          target_language: i18n?.language?.substring(0, 2) || 'fi',
        },
        { headers: { 'X-CSRFToken': getCsrfToken() } }
      );

      const translations = response.data.translations;
      setLines((prev) =>
        prev.map((line, i) => {
          const translated = translations[i];
          if (!translated || translated === line.name) return line;
          return { ...line, name: translated };
        })
      );
      setNamesTranslated(true);
    } catch (err) {
      console.error('Part name translation failed:', err);
      setConfirmError(t('inventory.smartReceive.translationFailed'));
    } finally {
      setTranslating(false);
    }
  };

  const handleConfirm = async () => {
    if (selectedCount === 0) return;

    setConfirming(true);
    setConfirmError(null);

    const confirmedLines = lines
      .filter((line) => selectedLines[line.line_index])
      .map((line) => ({
        line_index: line.line_index,
        action: isReturn ? 'return' : 'receive',
        matched_part_id: line.matched_part_id,
        quantity: line.quantity,
        // Send the price exactly as it appears on the document. The backend
        // performs the single VAT conversion using the two fields below —
        // converting here as well is what understated stock value by ~20%.
        unit_cost: line.unit_price,
        price_includes_vat: !!line.price_includes_vat,
        vat_rate: line.vat_rate ?? DEFAULT_VAT_RATE,
        name: formattedPrefix ? `${formattedPrefix}${line.name}` : line.name,
        manufacturer_code: line.manufacturer_code,
        // Package → content: the unit rides along only when it differs from the
        // matched part's (or is not kpl on a new part). Absent = today's booking.
        ...(!isReturn && unitDiffers(line) ? { unit: line.unit } : {}),
        // Sell price rides along only when the user gave one — an absent key
        // means "don't touch". Sent as entered; the backend converts once,
        // same contract as unit_cost.
        ...(!isReturn && line.sell_price !== '' && line.sell_price != null && parseFloat(line.sell_price) > 0
          ? {
              sell_price: line.sell_price,
              sell_price_includes_vat: !!line.sell_price_includes_vat,
            }
          : {}),
      }));

    // Format date as YYYY-MM-DD string for the API
    let dateStr = null;
    if (transactionDate && isValid(transactionDate)) {
      dateStr = formatDate(transactionDate, 'yyyy-MM-dd');
    }

    try {
      const confirmEndpoint = isReturn ? 'smart-return' : 'smart-receive';
      const response = await axios.post(
        `/api/inventory/${confirmEndpoint}/confirm/?organization=${orgId}`,
        {
          extraction_id: extractionId,
          vendor_id: vendor?.id || null,
          reference_number: referenceNumber,
          transaction_date: dateStr,
          lines: confirmedLines,
        },
        {
          headers: { 'X-CSRFToken': getCsrfToken() },
        }
      );

      // Success - close dialog and notify parent
      onSuccess?.(response.data);
      handleClose();
    } catch (err) {
      console.error('Smart receive confirm failed:', err);
      setConfirmError(err.response?.data?.error || t('inventory.smartReceive.extractionFailed'));
    } finally {
      setConfirming(false);
    }
  };

  const resetBatchState = () => {
    setVatSwapped(false);
    setPriceIsSellPrice(false);
    setSellPrefilled(false);
    setTranslating(false);
    setNamesTranslated(false);
  };

  const handleClose = () => {
    setPhase('upload');
    setFile(null);
    setUploading(false);
    setUploadError(null);
    setVendor(null);
    setVendorInputValue('');
    setExtractedVendorName(null);
    setReferenceNumber('');
    setNamePrefix('');
    setTransactionDate(new Date());
    setExtractionId(null);
    setLines([]);
    setSelectedLines({});
    resetBatchState();
    setConfirmError(null);
    onClose();
  };

  const handleChangeFile = () => {
    setPhase('upload');
    setFile(null);
    setExtractionId(null);
    setLines([]);
    setSelectedLines({});
    resetBatchState();
    setUploadError(null);
    setConfirmError(null);
    setVendor(null);
    setVendorInputValue('');
    setExtractedVendorName(null);
    setReferenceNumber('');
    setNamePrefix('');
    setTransactionDate(new Date());
  };

  const getStatusChip = (matchStatus) => {
    if (isReturn) {
      switch (matchStatus) {
        case 'inventory_exists':
          return (
            <Chip
              size="small"
              label={t('inventory.smartReturn.summary.inventoryExists')}
              color="success"
              sx={{ fontSize: '11px' }}
            />
          );
        case 'part_exists':
          return (
            <Chip
              size="small"
              label={t('inventory.smartReturn.status.notTracked')}
              sx={{ fontSize: '11px', bgcolor: 'action.disabledBackground', color: 'text.disabled' }}
            />
          );
        case 'no_match':
          return (
            <Chip
              size="small"
              label={t('inventory.smartReturn.status.notFound')}
              sx={{ fontSize: '11px', bgcolor: 'action.disabledBackground', color: 'text.disabled' }}
            />
          );
        default:
          return null;
      }
    }
    switch (matchStatus) {
      case 'inventory_exists':
        return (
          <Chip
            size="small"
            label={t('inventory.smartReceive.status.inventoryExists')}
            color="success"
            sx={{ fontSize: '11px' }}
          />
        );
      case 'part_exists':
        return (
          <Chip
            size="small"
            label={t('inventory.smartReceive.status.partExists')}
            color="warning"
            sx={{ fontSize: '11px' }}
          />
        );
      case 'no_match':
        return (
          <Chip
            size="small"
            label={t('inventory.smartReceive.status.noMatch')}
            color="error"
            sx={{ fontSize: '11px' }}
          />
        );
      default:
        return null;
    }
  };

  const getProjectedStock = (line) => {
    if (line.match_status === 'inventory_exists' && line.current_stock != null) {
      const before = parseFloat(line.current_stock);
      const qty = parseFloat(line.quantity || 0);
      const after = isReturn ? before - qty : before + qty;
      const tKey = isReturn ? 'inventory.smartReturn.stockAfter' : 'inventory.smartReceive.stockAfter';
      return t(tKey, {
        before: before.toFixed(2),
        after: after.toFixed(2),
      });
    }
    return null;
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      data-testid="smart-receive-dialog"
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {isReturn ? <UndoIcon /> : <LocalShippingIcon />}
        {t(isReturn ? 'inventory.smartReturn.title' : 'inventory.smartReceive.title')}
      </DialogTitle>
      <DialogContent>
        {phase === 'upload' && !uploading && (
          <Box sx={{ pt: 1 }}>
            <Alert severity="info" sx={{ mb: isReturn ? 3 : 1.5 }}>
              {t(isReturn ? 'inventory.smartReturn.infoBox' : 'inventory.smartReceive.infoBox')}
            </Alert>

            {/* Steer bulk part catalogs away from the AI pipeline: one receive
                document at a time here, CSV import for mass data. */}
            {!isReturn && (
              <Alert severity="warning" sx={{ mb: 3 }} data-testid="smart-receive-not-mass-import">
                {t('inventory.smartReceive.notForMassImport')}
              </Alert>
            )}

            {/* Dropzone */}
            <Box
              {...getRootProps()}
              sx={{
                border: '2px dashed',
                borderColor: isDragActive ? 'primary.main' : 'divider',
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                cursor: 'pointer',
                bgcolor: isDragActive ? 'action.hover' : 'background.default',
                mb: 3,
                transition: 'all 0.2s',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'action.hover',
                },
              }}
              data-testid="smart-receive-dropzone"
            >
              <input {...getInputProps()} />
              <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {t('inventory.smartReceive.dropzone')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('inventory.smartReceive.supportedFormats')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('inventory.smartReceive.hint')}
              </Typography>
            </Box>

            {file && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {file.name} ({(file.size / 1024).toFixed(0)} KB)
              </Alert>
            )}

            {uploadError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {uploadError}
              </Alert>
            )}
          </Box>
        )}

        {/* Loading overlay */}
        {uploading && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 4,
            }}
          >
            <CircularProgress sx={{ mb: 2 }} />
            <Typography color="text.secondary">
              {t('inventory.smartReceive.processing')}
            </Typography>
          </Box>
        )}

        {phase === 'review' && (
          <Box sx={{ pt: 1 }}>
            {/* File info + change button */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                {file?.name}
              </Typography>
              <Button size="small" onClick={handleChangeFile}>
                {t('inventory.smartReceive.changeFile')}
              </Button>
            </Box>

            {/* Vendor, reference, date - pre-filled from AI, editable */}
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fi}>
              <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <Autocomplete
                  options={vendors}
                  getOptionLabel={(option) => {
                    if (option._isNew) return option.name;
                    return option.name || '';
                  }}
                  value={vendor}
                  onChange={handleVendorChange}
                  inputValue={vendorInputValue}
                  onInputChange={handleVendorInputChange}
                  loading={vendorLoading || creatingVendor}
                  filterOptions={(options, params) => {
                    const filtered = vendorFilter(options, params);
                    const { inputValue } = params;
                    // Suggest creating a new vendor if no exact match
                    const isExisting = options.some(
                      (option) => option.name?.toLowerCase() === inputValue.toLowerCase()
                    );
                    if (inputValue !== '' && !isExisting) {
                      filtered.push({
                        _isNew: true,
                        name: inputValue,
                      });
                    }
                    return filtered;
                  }}
                  renderOption={(props, option) => {
                    if (option._isNew) {
                      return (
                        <li {...props}>
                          <AddIcon sx={{ mr: 1, fontSize: 18, color: 'primary.main' }} />
                          <Typography variant="body2">
                            {t('inventory.smartReceive.createVendor', { name: option.name })}
                          </Typography>
                        </li>
                      );
                    }
                    return <li {...props}>{option.name}</li>;
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      autoComplete="off"
                      label={t('inventory.smartReceive.vendor')}
                      size="small"
                    />
                  )}
                  isOptionEqualToValue={(option, value) => option.id === value?.id}
                  disabled={confirming}
                  sx={{ minWidth: 200, flex: 1 }}
                  data-testid="smart-receive-vendor"
                />
                <TextField
                  label={t('inventory.smartReceive.reference')}
                  value={referenceNumber}
                  onChange={(e) => setReferenceNumber(e.target.value)}
                  size="small"
                  sx={{ minWidth: 150 }}
                  disabled={confirming}
                />
                <DatePicker
                  label={t('inventory.smartReceive.date')}
                  value={transactionDate}
                  onChange={(newValue) => setTransactionDate(newValue)}
                  format="dd.MM.yyyy"
                  disabled={confirming}
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: { minWidth: 160 },
                    },
                  }}
                />
              </Box>
              {!isReturn && (
                <TextField
                  label={t('inventory.smartReceive.namePrefix')}
                  value={namePrefix}
                  onChange={(e) => setNamePrefix(e.target.value)}
                  size="small"
                  fullWidth
                  placeholder={t('inventory.smartReceive.namePrefixPlaceholder')}
                  helperText={t('inventory.smartReceive.namePrefixHelp')}
                  disabled={confirming}
                  sx={{ mb: 1 }}
                />
              )}
            </LocalizationProvider>

            {/* Suggest creating vendor from extracted name */}
            {extractedVendorName && !vendor && (
              <Alert
                severity="info"
                sx={{ mb: 2 }}
                action={
                  <Button
                    color="inherit"
                    size="small"
                    disabled={creatingVendor}
                    onClick={async () => {
                      setCreatingVendor(true);
                      try {
                        const response = await axios.post(
                          `/api/vendors/?organization=${orgId}`,
                          { name: extractedVendorName },
                          { headers: { 'X-CSRFToken': getCsrfToken() } }
                        );
                        const created = response.data;
                        setVendor(created);
                        setVendorInputValue(created.name);
                        setExtractedVendorName(null);
                        fetchVendors('');
                      } catch (err) {
                        console.error('Failed to create vendor:', err);
                      } finally {
                        setCreatingVendor(false);
                      }
                    }}
                  >
                    {creatingVendor ? <CircularProgress size={16} /> : t('inventory.smartReceive.createExtractedVendor')}
                  </Button>
                }
              >
                {t('inventory.smartReceive.extractedVendor', { name: extractedVendorName })}
              </Alert>
            )}

            {confirmError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {confirmError}
              </Alert>
            )}

            {/* Batch corrections for what the AI read wrong */}
            <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
              <Typography sx={{ fontSize: '12px', color: 'text.secondary', mr: 0.5 }}>
                {vatSummary === 'mixed'
                  ? t('inventory.smartReceive.mixedVat')
                  : t('inventory.smartReceive.aiReadPrices', {
                      vat: vatSummary === 'incl' ? t('VAT 25.5%') : t('VAT 0%'),
                    })}
              </Typography>
              <Tooltip title={t('inventory.smartReceive.vatSwapTooltip')} arrow enterDelay={500}>
                <Button
                  size="small"
                  variant={vatSwapped ? 'contained' : 'outlined'}
                  color={vatSwapped ? 'warning' : 'inherit'}
                  startIcon={<SwapHorizIcon />}
                  onClick={handleVatSwap}
                  disabled={confirming}
                  data-testid="smart-receive-vat-swap"
                  sx={{ textTransform: 'none', fontSize: '12px' }}
                >
                  {t('inventory.smartReceive.swapVat')}
                </Button>
              </Tooltip>
              {hasAlternatePrice && (
                <Tooltip title={t('inventory.smartReceive.priceSwapTooltip')} arrow enterDelay={500}>
                  <Button
                    size="small"
                    variant={priceIsSellPrice ? 'contained' : 'outlined'}
                    color={priceIsSellPrice ? 'info' : 'inherit'}
                    startIcon={<SellIcon />}
                    onClick={handlePriceTypeSwap}
                    disabled={confirming}
                    data-testid="smart-receive-price-swap"
                    sx={{ textTransform: 'none', fontSize: '12px' }}
                  >
                    {priceIsSellPrice
                      ? t('inventory.smartReceive.usingListPrice')
                      : t('inventory.smartReceive.usingPurchasePrice')}
                  </Button>
                </Tooltip>
              )}
              {hasAlternatePrice && !isReturn && !priceIsSellPrice && (
                <Tooltip title={t('inventory.smartReceive.prefillSellTooltip')} arrow enterDelay={500}>
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      color="inherit"
                      startIcon={<SellIcon />}
                      onClick={handleSellPrefill}
                      disabled={sellPrefilled || confirming}
                      data-testid="smart-receive-sell-prefill"
                      sx={{ textTransform: 'none', fontSize: '12px' }}
                    >
                      {t('inventory.smartReceive.prefillSellFromList')}
                    </Button>
                  </span>
                </Tooltip>
              )}
              <Tooltip title={t('inventory.smartReceive.translateTooltip')} arrow enterDelay={500}>
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    color="inherit"
                    startIcon={translating ? <CircularProgress size={14} /> : <TranslateIcon />}
                    onClick={handleTranslateNames}
                    disabled={translating || namesTranslated || confirming}
                    data-testid="smart-receive-translate"
                    sx={{ textTransform: 'none', fontSize: '12px' }}
                  >
                    {namesTranslated
                      ? t('inventory.smartReceive.namesTranslated')
                      : t('inventory.smartReceive.translateNames')}
                  </Button>
                </span>
              </Tooltip>
            </Box>

            {/* Lines table */}
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" />
                    <TableCell>{t('inventory.smartReceive.columns.status')}</TableCell>
                    <TableCell>{t('inventory.smartReceive.columns.product')}</TableCell>
                    <TableCell>{t('inventory.smartReceive.columns.code')}</TableCell>
                    <TableCell align="right">{t('inventory.smartReceive.columns.quantity')}</TableCell>
                    <TableCell align="right">
                      {/* Toggle stacked under the label: side by side the two
                          price columns force horizontal scrolling. */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '13px' }}>
                          {t('inventory.smartReceive.columns.price')}
                        </Typography>
                        <VatModeToggle
                          value={vatSummary === 'mixed' ? null : vatSummary}
                          onChange={(e, val) => val && setAllLinesVat(val === 'incl')}
                          disabled={confirming}
                          sx={{ flexShrink: 0 }}
                          help={false}
                        />
                      </Box>
                    </TableCell>
                    {!isReturn && (
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                          <Tooltip title={t('inventory.smartReceive.sellPriceHint')} arrow enterDelay={500}>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '13px' }}>
                              {t('inventory.smartReceive.columns.sellPrice')}
                            </Typography>
                          </Tooltip>
                          <VatModeToggle
                            value={sellVatSummary === 'mixed' ? null : sellVatSummary}
                            onChange={(e, val) => val && setAllLinesSellVat(val === 'incl')}
                            disabled={confirming}
                            sx={{ flexShrink: 0 }}
                            data-testid="smart-receive-sell-vat-toggle"
                            help={false}
                          />
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lines.map((line) => {
                    const projectedStock = getProjectedStock(line);
                    const isNoMatch = line.match_status === 'no_match';
                    const isReturnable = line.match_status === 'inventory_exists';
                    const lineDisabled = isReturn && !isReturnable;

                    // Sell-price cell context: entered value > existing fixed
                    // sell price > margin-derived preview from the current cost.
                    const hasEnteredSell = line.sell_price != null && line.sell_price !== '';
                    const currentSellDisplay =
                      line.part_sell_price_entered_with_vat && line.part_stored_sell_price_incl_vat != null
                        ? line.part_stored_sell_price_incl_vat
                        : line.part_sell_price;
                    let marginDerivedSell = null;
                    if (
                      !isReturn && !hasEnteredSell && currentSellDisplay == null &&
                      !line.part_is_margin_vat_scheme &&
                      line.effective_margin_percent != null &&
                      line.unit_price != null && line.unit_price !== ''
                    ) {
                      const netCost = line.price_includes_vat
                        ? calcPriceExclVat(line.unit_price, line.vat_rate ?? DEFAULT_VAT_RATE)
                        : parseFloat(line.unit_price);
                      if (!isNaN(netCost)) {
                        const netSell = netCost * (1 + line.effective_margin_percent / 100);
                        marginDerivedSell = line.sell_price_includes_vat
                          ? calcPriceInclVat(netSell, DEFAULT_VAT_RATE)
                          : netSell;
                      }
                    }

                    return (
                      <TableRow
                        key={line.line_index}
                        data-testid={`smart-receive-line-${line.line_index}`}
                        sx={{
                          opacity: selectedLines[line.line_index] && !lineDisabled ? 1 : 0.5,
                        }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={!!selectedLines[line.line_index]}
                            onChange={() => handleLineToggle(line.line_index)}
                            disabled={confirming || lineDisabled}
                            data-testid={`smart-receive-check-${line.line_index}`}
                          />
                        </TableCell>
                        <TableCell>{getStatusChip(line.match_status)}</TableCell>
                        <TableCell>
                          {isNoMatch && !isReturn ? (
                            <Box>
                              {formattedPrefix && (
                                <Typography variant="caption" color="primary.main" sx={{ fontWeight: 500 }}>
                                  {formattedPrefix}
                                </Typography>
                              )}
                              <TextField
                                value={line.name}
                                onChange={(e) => handleLineEdit(line.line_index, 'name', e.target.value)}
                                size="small"
                                variant="standard"
                                fullWidth
                                disabled={confirming}
                              />
                            </Box>
                          ) : (
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {formattedPrefix && <Typography component="span" variant="body2" color="primary.main" sx={{ fontWeight: 500 }}>{formattedPrefix}</Typography>}
                                {line.matched_part_name || line.name}
                              </Typography>
                              {line.matched_part_name && line.matched_part_name !== line.name && (
                                <Typography variant="caption" color="text.secondary">
                                  {line.name}
                                </Typography>
                              )}
                            </Box>
                          )}
                          {/* Helper text */}
                          {projectedStock && (
                            <Typography variant="caption" color={isReturn ? 'warning.main' : 'success.main'} sx={{ display: 'block' }}>
                              {projectedStock}
                            </Typography>
                          )}
                          {line.match_status === 'part_exists' && !isReturn && (
                            <Typography variant="caption" color="warning.main" sx={{ display: 'block' }}>
                              {t('inventory.smartReceive.trackingWillStart')}
                            </Typography>
                          )}
                          {isNoMatch && !isReturn && (
                            <Typography variant="caption" color="error.main" sx={{ display: 'block' }}>
                              {t('inventory.smartReceive.willCreatePart')}
                              {line.unit && line.unit !== 'kpl' ? ` (${line.unit})` : ''}
                            </Typography>
                          )}
                          {!isReturn && !isNoMatch && unitDiffers(line) && (() => {
                            const twin = line.package_content?.content_part_info;
                            const target =
                              line.content_part || (twin && twin.unit === line.unit ? twin : null);
                            const candidates = (line.package_content?.candidates || []).filter(
                              (c) => c.unit === line.unit
                            );
                            return (
                              <Box data-testid={`smart-receive-content-part-${line.line_index}`} sx={{ mt: 0.5 }}>
                                <Typography variant="caption" color="info.main" sx={{ display: 'block' }}>
                                  {target
                                    ? t('inventory.smartReceive.contentPartExisting', { name: target.name, unit: line.unit })
                                    : t('inventory.smartReceive.contentPartNew', { unit: line.unit })}
                                </Typography>
                                {(!twin || twin.unit !== line.unit) && candidates.length > 0 && (
                                  <Autocomplete
                                    size="small"
                                    options={candidates}
                                    value={line.content_part}
                                    getOptionLabel={(c) => c.name}
                                    isOptionEqualToValue={(a, b) => a.id === b.id}
                                    onChange={(_, value) => handleContentPartPick(line.line_index, value)}
                                    disabled={confirming}
                                    sx={{ mt: 0.5, maxWidth: 260 }}
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        variant="standard"
                                        placeholder={t('inventory.smartReceive.contentPartPick', { unit: line.unit })}
                                        inputProps={{
                                          ...params.inputProps,
                                          'data-testid': `smart-receive-content-part-pick-${line.line_index}`,
                                        }}
                                      />
                                    )}
                                  />
                                )}
                              </Box>
                            );
                          })()}
                          {lineDisabled && (
                            <Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>
                              {t('inventory.smartReturn.cannotReturn')}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {isNoMatch && !isReturn ? (
                            <TextField
                              value={line.manufacturer_code}
                              onChange={(e) =>
                                handleLineEdit(line.line_index, 'manufacturer_code', e.target.value)
                              }
                              size="small"
                              variant="standard"
                              sx={{ width: 120 }}
                              disabled={confirming}
                            />
                          ) : (
                            <Typography
                              variant="body2"
                              sx={{ ...codeFont, fontSize: '12px' }}
                            >
                              {line.manufacturer_code || '-'}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <DecimalField
                            value={line.quantity}
                            onChange={(val) => handleLineEdit(line.line_index, 'quantity', val)}
                            size="small"
                            sx={{ width: isReturn ? 80 : 120 }}
                            disabled={confirming}
                            inputProps={{ 'data-testid': `smart-receive-qty-${line.line_index}` }}
                            InputProps={
                              isReturn
                                ? undefined
                                : {
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        <Button
                                          size="small"
                                          onClick={(e) => setUnitMenu({ anchor: e.currentTarget, lineIndex: line.line_index })}
                                          disabled={confirming}
                                          sx={{ minWidth: 0, px: 0.5, fontSize: '11px', textTransform: 'none' }}
                                          data-testid={`smart-receive-unit-${line.line_index}`}
                                        >
                                          {line.unit || 'kpl'}
                                        </Button>
                                      </InputAdornment>
                                    ),
                                  }
                            }
                          />
                          {/* Package → content: a size token in the name ("20L") offers
                              booking the row as its content instead of 1 kpl. */}
                          {!isReturn && line.package_content && (
                            <ToggleButtonGroup
                              size="small"
                              exclusive
                              value={line.booking_mode === 'content' ? 'content' : 'package'}
                              onChange={(_, value) => value && handleBookingModeChange(line.line_index, value)}
                              disabled={confirming}
                              sx={{ mt: 0.5 }}
                            >
                              {packageUnit(line) === 'kpl' && (
                                <ToggleButton
                                  value="package"
                                  data-testid={`smart-receive-book-${line.line_index}-package`}
                                  sx={{ py: 0, px: 0.75, fontSize: '11px', textTransform: 'none' }}
                                >
                                  {t('inventory.smartReceive.bookAsPackage', {
                                    qty: line.booking_mode === 'content' ? line.package_quantity : line.quantity,
                                  })}
                                </ToggleButton>
                              )}
                              <ToggleButton
                                value="content"
                                data-testid={`smart-receive-book-${line.line_index}-content`}
                                sx={{ py: 0, px: 0.75, fontSize: '11px', textTransform: 'none' }}
                              >
                                {t('inventory.smartReceive.bookAsContent', {
                                  qty: parseFloat(line.package_content.booked_quantity),
                                  unit: line.package_content.unit,
                                })}
                              </ToggleButton>
                            </ToggleButtonGroup>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <DecimalField
                            value={line.unit_price ?? ''}
                            onChange={(val) => handleLineEdit(line.line_index, 'unit_price', val)}
                            size="small"
                            sx={{ width: 90 }}
                            disabled={confirming}
                            inputProps={{ 'data-testid': `smart-receive-price-${line.line_index}` }}
                          />
                          {line.unit_price != null && line.unit_price !== '' && (
                            <Typography sx={{ fontSize: '11px', color: 'text.secondary', mt: 0.25 }}>
                              {line.price_includes_vat
                                ? `${t('excl. VAT')}: ${formatVatPrice(calcPriceExclVat(line.unit_price, line.vat_rate ?? DEFAULT_VAT_RATE))} €`
                                : `${t('incl. VAT')}: ${formatVatPrice(calcPriceInclVat(line.unit_price, line.vat_rate ?? DEFAULT_VAT_RATE))} €`}
                            </Typography>
                          )}
                        </TableCell>
                        {!isReturn && (
                          <TableCell align="right">
                            <DecimalField
                              value={line.sell_price ?? ''}
                              onChange={(val) => handleLineEdit(line.line_index, 'sell_price', val)}
                              size="small"
                              sx={{ width: 90 }}
                              disabled={confirming || !!line.part_is_margin_vat_scheme}
                              placeholder={currentSellDisplay != null ? formatVatPrice(currentSellDisplay) : undefined}
                              data-testid={`smart-receive-sell-${line.line_index}`}
                            />
                            {line.unit && line.unit !== 'kpl' && (
                              <Typography sx={{ fontSize: '11px', color: 'text.secondary', mt: 0.25 }}>
                                {t('inventory.smartReceive.sellPricePerUnit', { unit: line.unit })}
                              </Typography>
                            )}
                            {line.part_is_margin_vat_scheme ? (
                              <Typography sx={{ fontSize: '11px', color: 'text.secondary', mt: 0.25 }}>
                                {t('inventory.smartReceive.marginSchemePart')}
                              </Typography>
                            ) : hasEnteredSell ? (
                              <Typography sx={{ fontSize: '11px', color: 'text.secondary', mt: 0.25 }}>
                                {line.sell_price_includes_vat
                                  ? `${t('excl. VAT')}: ${formatVatPrice(calcPriceExclVat(line.sell_price, DEFAULT_VAT_RATE))} €`
                                  : `${t('incl. VAT')}: ${formatVatPrice(calcPriceInclVat(line.sell_price, DEFAULT_VAT_RATE))} €`}
                              </Typography>
                            ) : currentSellDisplay != null ? (
                              <Typography sx={{ fontSize: '11px', color: 'text.secondary', mt: 0.25 }}>
                                {t('inventory.smartReceive.currentSellPrice', {
                                  price: formatVatPrice(currentSellDisplay),
                                })}
                              </Typography>
                            ) : marginDerivedSell != null ? (
                              <Typography sx={{ fontSize: '11px', color: 'text.secondary', mt: 0.25 }}>
                                {t('inventory.smartReceive.marginSellPrice', {
                                  margin: formatVatPrice(line.effective_margin_percent, 0),
                                  price: formatVatPrice(marginDerivedSell),
                                })}
                              </Typography>
                            ) : null}
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Summary legend */}
            <Box sx={{ display: 'flex', gap: 3, mt: 2, flexWrap: 'wrap' }}>
              {statusCounts.inventory_exists > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main' }} />
                  <Typography variant="caption">
                    {t(isReturn ? 'inventory.smartReturn.summary.inventoryExists' : 'inventory.smartReceive.summary.inventoryExists')} ({statusCounts.inventory_exists})
                  </Typography>
                </Box>
              )}
              {statusCounts.part_exists > 0 && !isReturn && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'warning.main' }} />
                  <Typography variant="caption">
                    {t('inventory.smartReceive.summary.partExists')} ({statusCounts.part_exists})
                  </Typography>
                </Box>
              )}
              {statusCounts.no_match > 0 && !isReturn && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'error.main' }} />
                  <Typography variant="caption">
                    {t('inventory.smartReceive.summary.noMatch')} ({statusCounts.no_match})
                  </Typography>
                </Box>
              )}
              {isReturn && (statusCounts.part_exists > 0 || statusCounts.no_match > 0) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'action.disabled' }} />
                  <Typography variant="caption">
                    {t('inventory.smartReturn.summary.notReturnable')} ({statusCounts.part_exists + statusCounts.no_match})
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={uploading || confirming}>
          {t('inventory.smartReceive.actions.cancel')}
        </Button>
        {phase === 'review' && (
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={selectedCount === 0 || confirming}
            data-testid="smart-receive-confirm-btn"
          >
            {confirming ? (
              <CircularProgress size={20} />
            ) : (
              t(isReturn
                ? 'inventory.smartReturn.actions.returnCount'
                : 'inventory.smartReceive.actions.receiveCount',
                { count: selectedCount })
            )}
          </Button>
        )}
      </DialogActions>
      {/* Unit picker for the quantity field's "kpl" adornment (receive mode). */}
      <Menu
        open={!!unitMenu}
        anchorEl={unitMenu?.anchor}
        onClose={() => setUnitMenu(null)}
        data-testid="smart-receive-unit-menu"
      >
        {UNIT_OPTIONS.map((u) => (
          <MenuItem
            key={u}
            dense
            onClick={() => handleUnitPick(unitMenu.lineIndex, u)}
            data-testid={`smart-receive-unit-option-${u}`}
          >
            {u} — {t('unit_' + u)}
          </MenuItem>
        ))}
      </Menu>
    </Dialog>
  );
}

export default withTranslation()(SmartReceiveDialog);
